import { createTheme } from "@mui/material/styles";
import LincolnMillerRoman from "./fonts/lincoln/LincolnMillerBWGL-Roman/LincolnMillerBWGL-Roman.woff2";
import LincolnMillerSemibold from "./fonts/lincoln/LincolnMillerBWGL-Semibold/LincolnMillerBWGL-Semibold.woff2";
import ProximaNovaRegular from "./fonts/lincoln/ProximaNovaRegular/ProximaNova-Reg-webfont.woff";
import ProximaNovaSemibold from "./fonts/lincoln/ProximaNovaSemibold/ProximaNova-Sbold-webfont.woff";
import ProximaNovaBold from "./fonts/lincoln/ProximaNovaBold/ProximaNova-Bold-webfont.woff";

const lincolnMillerRoman = {
  fontFamily: "LincolnMillerBWGLRoman",
  fontDisplay: "swap",
  fontWeight: 400,
  src: `url(${LincolnMillerRoman}) format('woff2')`,
};

const lincolnMillerSemibold = {
  fontFamily: "LincolnMillerBWGLSemibold",
  fontDisplay: "swap",
  fontWeight: 600,
  src: `url(${LincolnMillerSemibold}) format('woff2')`,
};

const proximaNovaRegular = {
  fontFamily: "ProximaNovaRegular",
  fontDisplay: "swap",
  fontWeight: 400,
  src: `url(${ProximaNovaRegular}) format('woff')`,
};

const proximaNovaSemibold = {
  fontFamily: "ProximaNovaSemibold",
  fontDisplay: "swap",
  fontWeight: 600,
  src: `url(${ProximaNovaSemibold}) format('woff')`,
};

const proximaNovaBold = {
  fontFamily: "ProximaNovaBold",
  fontDisplay: "swap",
  fontWeight: 700,
  src: `url(${ProximaNovaBold}) format('woff')`,
};

const baseLincolnTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 960,
      lg: 1440,
      xl: 1920,
    },
  },
  typography: {
    h1: {
      fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
      fontSize: 36,
      lineHeight: "44px",
      letterSpacing: "normal",
      fontWeight: 600,
    },
    h2: {
      fontFamily: "LincolnMillerBWGLSemibold, Arial, sans-serif",
      fontSize: 32,
      lineHeight: "40px",
      letterSpacing: "normal",
    },
    h3: {
      fontFamily: "LincolnMillerBWGLRoman, Arial, sans-serif",
      fontSize: 28,
      lineHeight: "36px",
      letterSpacing: "normal",
      fontWeight: 400,
    },
    h4: {
      fontFamily: "LincolnMillerBWGLRoman, Arial, sans-serif",
      fontSize: 24,
      lineHeight: "32px",
      letterSpacing: "normal",
    },
    h5: {
      fontFamily: "LincolnMillerBWGLRoman, Arial, sans-serif",
      fontSize: 20,
      lineHeight: "28px",
      letterSpacing: "normal",
    },
    h6: {
      fontFamily: "LincolnMillerBWGLSemibold, Arial, sans-serif",
      fontSize: 16,
      lineHeight: "24px",
      letterSpacing: "normal",
    },
    body1: {
      fontFamily: "ProximaNovaRegular, Arial, sans-serif",
      fontSize: 16,
      lineHeight: "24px",
      letterSpacing: "normal",
      fontWeight: 400,
    },
    body2: {
      fontFamily: "ProximaNovaRegular, Arial, sans-serif",
      fontSize: 14,
      lineHeight: "24px",
      letterSpacing: "normal",
      fontWeight: 400,
    },
    subtitle1: {
      fontFamily: "ProximaNovaBold, Arial, sans-serif",
      fontSize: 16,
      lineHeight: "24px",
      letterSpacing: "normal",
    },
    subtitle2: {
      fontFamily: "ProximaNovaBold, Arial, sans-serif",
      fontSize: 14,
      lineHeight: "24px",
      letterSpacing: "normal",
    },
    caption: {
      fontFamily: "ProximaNovaRegular, Arial, sans-serif",
      fontSize: 12,
      lineHeight: "16px",
      letterSpacing: "normal",
      fontWeight: 400,
      color: "#666666",
    },
    button: {
      fontFamily: "ProximaNovaBold, Arial, sans-serif",
      lineHeight: "20px",
      textTransform: "none",
      letterSpacing: "normal",
    },
  },
  spacing: 4,
  palette: {
    primary: {
      main: "#324047",
      dark: "#233338",
    },
    info: {
      main: "#E4F1FF",
      dark: "#00142E",
    },
    success: {
      main: "#E8F5E9",
      dark: "#61ba00",
    },
    warning: {
      main: "#FCF2E6",
    },
    error: {
      main: "#FFEBEE",
      dark: "#f26147",
    },
    text: {
      primary: "#000000",
      secondary: "#333333",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "@global": {
          "@font-face": [
            lincolnMillerRoman,
            lincolnMillerSemibold,
            proximaNovaRegular,
            proximaNovaSemibold,
            proximaNovaBold,
          ],
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        content: {
          margin: 0,
          "&.Mui-expanded": {
            margin: 0,
          },
        },
        expandIconWrapper: {
          transition: "none",
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: "0px 16px",
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderTop: "1px solid #6B7786",
          "&:first-of-type": {
            borderRadius: "0px",
          },
        },
        summaryRoot: {
          padding: "24px 16px",
          "&.Mui-expanded": {
            minHeight: "unset",
          },
        },
        content: {
          display: "block",
          margin: 0,
          color: "#1F3047",
          transition: "color 0.1s ease-in-out",
          "&.Mui-expanded": {
            margin: 0,
            color: "#324047",
          },
        },
        expandIcon: {
          "&.Mui-expanded": {
            transform: "unset",
          },
        },
        detailsRoot: {
          display: "block",
          padding: 16,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {},
      },
    },
    MuiAlert: {
      styleOverrides: {
        action: {
          padding: 0,
          margin: 0,
          marginLeft: 24,
          "& .MuiIconButton-root": {
            padding: 0,
            "&:hover": {
              backgroundColor: "transparent",
            },
          },
        },
        icon: {
          paddingTop: 0,
          paddingBottom: 0,
          opacity: 1,
          fontSize: 24,
          marginRight: 16,
        },
        message: {
          padding: 0,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
        disableTouchRipple: true,
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          padding: "10px 24px",
          "&.Mui-disabled": {
            background: "rgb(217, 217, 217)",
            color: "rgb(155, 155, 155)",
          },
        },
        text: {
          padding: "10px 0px",
          "&:hover": {
            background: "none",
          },
          "&.Mui-disabled": {
            background: "none",
          },
        },
        outlined: {
          "&.Mui-disabled": {
            background: "none",
            color: "rgb(155, 155, 155)",
            borderColor: "rgb(155,155,155)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
          letterSpacing: "normal",
          fontSize: 16,
          lineHeight: "24px",
          color: "#333333",
          borderRadius: 4,
        },
        label: {
          paddingLeft: 8,
          paddingRight: 8,
        },
        colorPrimary: {
          backgroundColor: "#E8F5E9",
          border: "1px solid #324047",
          color: "#000000",
        },
        colorSecondary: {
          backgroundColor: "#E5E5E5",
          border: "1px solid #808080",
          color: "#000000",
        },
        outlined: {
          fontSize: "12px",
          height: "auto",
          borderRadius: "8px",
          padding: "8px",
          lineHeight: "16px",
          border: "1px solid #e5e5e5",
          "& .MuiChip-labelSmall": {
            padding: 0,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          width: 24,
          height: 24,
          outline: "1px solid #808080",
          boxSizing: "border-box",
          "input:disabled ~ &": {
            backgroundColor: "#E5E5E5",
            border: "1px solid #808080",
          },
          "&.Mui-checked": {
            outline: "1px solid #324047",
            backgroundColor: "#324047",
            fontSize: "0px",
            "& svg": {
              color: "#FFFFFF",
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "16px",
        },
        dialogContent: {
          padding: "0px 16px 24px 16px",
        },
        actions: {
          padding: "16px",

          "&.MuiDialogActions-spacing > :last-child": {
            marginLeft: 16,
          },
        },
        edgeEnd: {
          padding: 0,

          "&:hover": {
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
          fontSize: 16,
          fontWeight: "400",
          lineHeight: "20px",
        },
        input: {
          marginTop: 0,
          borderRadius: 0,
          "&::placeholder": {
            fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
            fontSize: 16,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          borderRadius: 8,
          outline: "1px solid #6B7786",
          color: "#00142E",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 14,
          lineHeight: "20px",
          marginBottom: 4,
        },
        formControl: {
          display: "inline",
          position: "relative",
          top: "unset",
          left: "unset",
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          display: "inline",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiFormHelperText-root": {
            fontSize: 12,
            fontWeight: "400",
            lineHeight: "16px",
            marginBottom: 0,

            "&.Mui-disabled": {
              color: "#666666",
            },

            "&.Mui-error": {
              color: "#f26147",
            },
          },
          "& .MuiInputBase-root": {
            fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
            borderRadius: 8,
            outline: "1px solid #6B7786",
            padding: "8px 16px",
            backgroundColor: "#FFFFFF",
            fontWeight: "400",
            lineHeight: "20px",
            color: "#00142E",
          },
          "& .Mui-error.MuiInputBase-root": {
            borderRadius: 8,
            outline: "1px solid #f26147",
          },
          "& .MuiInputBase-input": {
            padding: 0,
          },
          "& .Mui-focused.MuiInputBase-root": {
            outline: "2px solid #324047",
          },
          "& .Mui-error.Mui-focused.MuiInputBase-root": {
            outline: "2px solid #f26147",
          },
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        containerRoot: {
          left: 0,
          bottom: 0,
        },
        root: {
          fontFamily: "'ProximaNovaSemibold', sans-serif",
          backgroundColor: "inherit",
          color: "inherit",
          width: "100%",
          fontSize: 16,
          padding: 16,
          borderRadius: 0,
        },
        action: {
          marginRight: 0,
          marginLeft: 20,
          padding: 0,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: "linear-gradient(135deg, #F5F6F7 0%, #FFFFFF 100%)",
          border: "none",
          borderRadius: 8,
          padding: "24px 16px",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {},
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: "4px",
          transition: "none",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
          lineHeight: "28px",
          fontSize: "20px",
          color: "#333333",
          padding: "8px 0px",
          alignItems: "flex-start",
          borderBottom: "2px solid #000000",

          "&:last-of-type": {
            marginRight: 0,
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: "0px",
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          lineHeight: "20px",
        },
        li: {
          margin: "0px 2px",
          fontSize: "14px",
          "&:not(:last-of-type) > a": {
            color: "#324047",
          },
        },
      },
    },
    MuiNativeSelect: {
      styleOverrides: {
        root: {
          marginTop: 0,
          "&:focus": {
            backgroundColor: "none",
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          paddingTop: "8px",
          paddingBottom: "8px",
          borderRadius: "8px",
          color: "black",
          border: "none",
          "&.Mui-selected": {
            backgroundColor: "#324047",
            color: "#ffffff",

            "&:hover": {
              backgroundColor: "#324047",
              color: "#ffffff",
            },
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
          border: "1px solid #b2b2b2",
          padding: 2,
        },
        fullWidth: {
          borderRadius: "10px",
        },
        firstButton: {
          borderTopRightRadius: "8px",
          borderBottomRightRadius: "8px",
          marginRight: "1px",
        },
        lastButton: {
          borderTopLeftRadius: "8px",
          borderBottomLeftRadius: "8px",
          marginLeft: "1px",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          fontWeight: 400,
          fontSize: 14,
          lineHeight: "20px",
          color: "#00142E",
        },
        outlined: {
          borderRadius: 8,
          border: "solid 1px #6B7786",
          backgroundColor: "white",
          boxSizing: "border-box",
          padding: "8px 16px",
          height: "auto",
          "&:focus": {
            borderRadius: 8,
            backgroundColor: "white",
            outline: "2px solid #324047",
          },
        },
        "&.Mui-disabled": {
          backgroundColor: "#E6E8EA",
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 48,
          height: 26,
          padding: 0,
          "& .MuiSwitch-switchBase": {
            padding: 0,
            margin: 2,
            transitionDuration: "300ms",
            "&.Mui-checked": {
              transform: "translateX(22px)",
              color: "#fff",
              "& .MuiSwitch-thumb:before": {
                backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
                  "#324047"
                )}" d="M16.7394 6.13312L15.635 5.02812L7.90062 12.7619L4.365 9.22625L3.26062 10.3312L7.90062 14.9719L16.7394 6.13312Z"/></svg>')`,
              },
              "& + .MuiSwitch-track": {
                backgroundColor: "#324047",
                opacity: 1,
                border: 0,
              },
              "&.Mui-disabled + .MuiSwitch-track": {
                opacity: 0.5,
              },
            },
            "&.Mui-focusVisible .MuiSwitch-thumb": {
              color: "#33cf4d",
              border: "6px solid #fff",
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: "#808080",
            },
            "&.Mui-disabled + .MuiSwitch-track": {
              opacity: 0.7,
            },
          },
          "& .MuiSwitch-thumb": {
            boxSizing: "border-box",
            width: 22,
            height: 22,
            "&::before": {
              content: "''",
              position: "absolute",
              width: "100%",
              height: "100%",
              left: 0,
              top: 0,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 48 48"><path fill="${encodeURIComponent(
                "#808080"
              )}" d="M25.68 24L36 34.32L34.32 36L24 25.68L13.68 36L12 34.32L22.32 24L12 13.68L13.68 12L24 22.32L34.32 12L36 13.68L25.68 24Z"/></svg>')`,
            },
          },
          "& .MuiSwitch-track": {
            borderRadius: 26 / 2,
            backgroundColor: "#808080",
            opacity: 1,
            transition: "background-color 0.5s ease-in-out",
          },
        },
      },
    },
  },
});

const LincolnTheme = createTheme(baseLincolnTheme, {
  palette: {
    gray: baseLincolnTheme.palette.augmentColor({
      color: {
        main: "#e5e5e5",
      },
      name: "gray",
    }),
  },
});

export default LincolnTheme;
